import { createSlice } from '@reduxjs/toolkit';
import { OrderLoadStatus, Page } from '~src/domains/common/enums/api';
import { Currency, Locale } from '~src/domains/common/enums/localization';
import { XhrResponseType } from '~src/domains/common/types/api';
import { CommonState } from '~src/domains/common/types/common';
import { CustomerType } from '~src/domains/common/types/users';
import {
  chooseDefaultOffer,
  mapTravelers,
  sortLoanOffers,
} from '~src/utils/helpers';
import { Brand } from '../enums/common';

export const initialState: CommonState = {
  // Merchant info
  merchant: null,
  paymentOptions: [],
  upcode: null,
  isOnboardSpend: null,
  // Customer info
  customerInfo: {},
  cardType: null,
  last4: null,
  plaidDisabled: false,
  plaidLinkToken: null,
  // Order info
  brand: Brand.UPLIFT,
  campaigns: [],
  offerId: '',
  orderLoadStatus: OrderLoadStatus.NOT_STARTED,
  travelers: {},
  airReservations: [],
  hotelReservations: [],
  cruiseReservations: [],
  options: [],
  page: Page.INITIAL,
  previousPage: null,
  locale: Locale.enUS,
  currency: Currency.USD,
  isOfferExpired: false,
  session: null,
  next: null,
  selectedTraveler: -1,
  isMultipleOfferFlow: false,
  loanOfferSelection: null,
  autoPayEnabled: true,
  // Status info
  mcode: null,
  pollCallCount: 0,
  showBanner: false,
  statusCode: 0,
  isProcessing: false,
  lazyDocsInFlight: false,
  showAlert: false,
  document: {
    id: null,
    content: null,
    url: null,
    show: false,
    loading: false,
  },
  documentRef: null,
  lazyDocs: {},
  bankMetadata: null,
  paymentMethod: null,
  secureForms: {
    initialized: false,
    dispatched: false,
  },
  lockoutSeconds: 0,
  lockoutMultiplier: 1,
  tooltipContainerRef: null,
};

export const commonSlice: any = createSlice({
  name: 'common',
  initialState,
  reducers: {
    general: (state, action) => {
      const payload = action.payload;
      // Track previous page when page changes
      if (payload.page !== undefined && payload.page !== state.page) {
        return { ...state, previousPage: state.page, ...payload };
      }
      return { ...state, ...payload };
    },
    initLockoutTimer: (state, action) => {
      const lockoutSeconds = action.payload;
      state.lockoutSeconds = lockoutSeconds;
      state.lockoutMultiplier = state.lockoutMultiplier + 1;
    },
    resetLockoutTimer: (state, action) => {
      state.lockoutSeconds = 0;
      state.lockoutMultiplier = 1;
    },
    setLockoutTimer: (state, action) => {
      state.lockoutSeconds = action.payload;
    },
    setBankMetadata: (state, action) => {
      state.bankMetadata = action.payload;
    },
    setCustomerInfo: (state, action) => {
      const contactInfo = action.payload;

      Object.keys(contactInfo).forEach(key => {
        state.customerInfo[key as keyof CustomerType] = contactInfo[key];
      });
    },
    setDocument: (state, action) => {
      state.document = action.payload;
    },
    setDocumentRef: (state, action) => {
      state.documentRef = action.payload;
    },
    setIsProcessing: (state, action) => {
      state.isProcessing = action.payload;
    },
    setMcode: (state, action) => {
      state.mcode = action.payload;
    },
    setMerchantConfig: (state, action) => {
      const config = action.payload;

      state.merchant = config.merchant;
      state.upcode = config.upcode;
      state.paymentOptions = config.paymentOptions;
      state.isOnboardSpend = config.onboardSpend;
      state.subventionPromo = config.subventionPromo;
    },
    setLazyDocs: (state, action) => {
      state.lazyDocs = {
        ...state.lazyDocs,
        ...action.payload,
      };
    },
    resetLazyDocs: (state, action) => {
      state.lazyDocs = {};
    },
    setLoanOfferSelection: (state, action) => {
      state.loanOfferSelection = action.payload;
    },
    setLocale: (state, action) => {
      state.locale = action.payload;
    },
    setOfferExpired: state => {
      state.isOfferExpired = true;
    },
    setOfferId: (state, action) => {
      state.offerId = action.payload;
    },
    setOrder: (state, action) => {
      const orderPayload: XhrResponseType = action.payload;
      const page = orderPayload.page;
      const session = orderPayload.session;
      const orderInfo = orderPayload.order_info;
      const mcode = orderPayload.mcode;
      const sessionLoanOffers =
        session.product_offers?.offers || session.loan_offers;
      const loanOffers = sortLoanOffers(sessionLoanOffers);
      const selectedLoanOffer = chooseDefaultOffer(loanOffers, page);

      if (orderInfo) {
        const {
          localization,
          travelers,
          air_reservations,
          hotel_reservations,
          cruise_reservations,
          brand,
          campaigns,
        } = orderInfo;

        state.currency = localization.currency;
        state.locale =
          `${localization.language}-${localization.country}` as Locale;
        state.travelers = mapTravelers(travelers);
        state.airReservations = air_reservations;
        state.hotelReservations = hotel_reservations;
        state.cruiseReservations = cruise_reservations;
        state.brand = brand;
        state.campaigns = campaigns;
      } else {
        state.locale = Locale.enUS;
      }

      state.session = session;
      state.options = orderPayload.options;
      state.previousPage = state.page;
      state.page = page;
      state.next = orderPayload.next;
      state.lazyDocsInFlight = page === Page.LOAN_CONSENT;
      state.selectedTraveler = 0;
      state.showAlert = false;
      state.isMultipleOfferFlow = loanOffers.length > 1;
      state.loanOfferSelection = selectedLoanOffer?.offer_id;
      state.autoPayEnabled =
        session['rege_opt-in'] != null ? session['rege_opt-in'] : true;

      if (mcode) state.mcode = mcode;
      if (page === Page.PAY) {
        state.last4 = session.card?.last4 || session.bank?.last4 || null;
        state.cardType = session.card?.type || null;
      }
    },
    setOrderLoadStatus: (state, action) => {
      state.orderLoadStatus = action.payload;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setPaymentMethod: (state, action) => {
      state.paymentMethod = action.payload;
    },
    setPlaidDisabled: state => {
      state.plaidDisabled = true;
    },
    setPlaidLinkToken: (state, action) => {
      state.plaidLinkToken = action.payload;
    },
    setPollCallCount: (state, action) => {
      state.pollCallCount = action.payload;
    },
    setSecureForms: (state, action) => {
      state.secureForms = {
        ...state.secureForms,
        ...action.payload,
      };
    },
    setShowBanner: (state, action) => {
      state.showBanner = action.payload;
    },
    setTooltipContainerRef: (state, action) => {
      state.tooltipContainerRef = action.payload;
    },
    setTripChanged: (state, action) => {
      const response = action.payload;
      const loanOffers =
        response.session?.product_offers?.offers ||
        response.session?.loan_offers ||
        [];
      const page = response.page;
      const selectedLoanOffer = chooseDefaultOffer(loanOffers, page);

      state.session = response.session;
      state.loanOfferSelection = selectedLoanOffer?.offer_id;
      state.isMultipleOfferFlow = loanOffers.length > 1;
      state.next = response.next;
      state.page = response.page;
    },
    updateOrder: (state, action) => {
      const orderPayload = action.payload;

      state.travelers = mapTravelers(orderPayload.travelers);
      state.airReservations = orderPayload.air_reservations;
      state.hotelReservations = orderPayload.hotel_reservations;
      state.cruiseReservations = orderPayload.cruise_reservations;
    },
  },
});

export const {
  general,
  initLockoutTimer,
  resetLockoutTimer,
  setLockoutTimer,
  setBankMetadata,
  setCustomerInfo,
  setDocument,
  setDocumentRef,
  setIsProcessing,
  setMcode,
  setMerchantConfig,
  setLazyDocs,
  resetLazyDocs,
  setLoanOfferSelection,
  setLocale,
  setOfferExpired,
  setOfferId,
  setOrder,
  setOrderLoadStatus,
  setPage,
  setPaymentMethod,
  setPlaidDisabled,
  setPlaidLinkToken,
  setPollCallCount,
  setSecureForms,
  setShowBanner,
  setTooltipContainerRef,
  setTripChanged,
  updateOrder,
} = commonSlice.actions;

export default commonSlice.reducer;
